import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Table, Button, Space, Form, Input, InputNumber, Select,
  message, Popconfirm, Card, Typography, Tag, DatePicker,
} from 'antd';
import {
  DeleteOutlined, CheckOutlined, CloseOutlined, EditOutlined,
  PlusOutlined, ArrowLeftOutlined, CalendarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from '../../services/api';
import { COLOR_PRIMARY } from '../../utils/colors';

const { Title, Text } = Typography;
const { Option } = Select;

const EXCLUDED_DRIVERS = ['external', 'shafiq', 'kaler', 'ravinder singh'];

// ─── Date list view ───────────────────────────────────────────────────────────

const DateListView = ({ onSelectDate }) => {
  const [dateSummaries, setDateSummaries] = useState([]);
  const [loading, setLoading] = useState(false);

  const today = dayjs().format('YYYY-MM-DD');

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await apiService.fuelRuns.getDates();
        const data = res.data || [];
        const hasToday = data.some(d => d.date === today);
        const list = hasToday ? data : [{ date: today, count: 0, total: 0 }, ...data];
        setDateSummaries(list);
      } catch {
        message.error('Failed to load dates');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [today]);

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (d) => (
        <Space>
          <Text strong>{dayjs(d).format('ddd DD MMM YYYY')}</Text>
          {d === today && <Tag color="blue">Today</Tag>}
        </Space>
      ),
    },
    {
      title: 'Entries',
      dataIndex: 'count',
      key: 'count',
      width: 90,
      align: 'center',
      render: (v) => <Text type={v === 0 ? 'secondary' : undefined}>{v}</Text>,
    },
    {
      title: 'Total Spent',
      dataIndex: 'total',
      key: 'total',
      width: 140,
      align: 'right',
      render: (v) => (
        <Text strong style={v > 0 ? { color: '#3f8600' } : {}}>
          {v > 0 ? `£${v.toFixed(2)}` : '—'}
        </Text>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <Button size="small" onClick={() => onSelectDate(record.date)}>
          {record.count === 0 ? 'Start' : 'Open'}
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Fuel Log</Title>
        <DatePicker
          placeholder="Go to date"
          format="DD MMM YYYY"
          disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
          onChange={(d) => d && onSelectDate(d.format('YYYY-MM-DD'))}
          allowClear={false}
          value={null}
        />
      </div>
      <Table
        dataSource={dateSummaries}
        columns={columns}
        rowKey="date"
        loading={loading}
        pagination={false}
        size="small"
        onRow={(record) => ({ onClick: () => onSelectDate(record.date), style: { cursor: 'pointer' } })}
        style={{ borderRadius: 8, overflow: 'hidden' }}
      />
    </div>
  );
};

// ─── Day detail view ──────────────────────────────────────────────────────────

const DayDetailView = ({ date, onBack }) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [editForm] = Form.useForm();
  const [addForm] = Form.useForm();
  const firstFieldRef = useRef(null);

  const dayjsDate = dayjs(date);

  useEffect(() => {
    apiService.drivers.getAll({ scope: 'all' }).then((res) => {
      const all = Array.isArray(res.data) ? res.data : res.data?.drivers || [];
      setDrivers(
        all
          .filter(dr => !EXCLUDED_DRIVERS.some(ex => dr.name?.toLowerCase().includes(ex)))
          .sort((a, b) => {
            const ao = a.org === 'opc' ? 0 : 1;
            const bo = b.org === 'opc' ? 0 : 1;
            return ao - bo || (a.name || '').localeCompare(b.name || '');
          })
      );
    }).catch(() => {});
  }, []);

  const fetchRuns = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.fuelRuns.getAll({ date });
      setRuns(res.data || []);
    } catch {
      message.error('Failed to fetch fuel runs');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const startEdit = (record) => {
    if (editingKey) cancelEdit();
    editForm.setFieldsValue({
      driver: record.driver?._id || undefined,
      amount: record.amount,
      notes: record.notes || '',
    });
    setEditingKey(record._id);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    editForm.resetFields();
  };

  const saveEdit = async (record) => {
    try {
      const values = await editForm.validateFields();
      await apiService.fuelRuns.update(record._id, values);
      setEditingKey(null);
      message.success('Saved');
      fetchRuns();
    } catch (e) {
      if (e?.response?.data?.message) message.error(e.response.data.message);
    }
  };

  const handleAdd = async () => {
    try {
      const values = await addForm.validateFields();
      await apiService.fuelRuns.create({ ...values, date: `${date}T12:00:00.000Z` });
      addForm.resetFields();
      fetchRuns();
      setTimeout(() => firstFieldRef.current?.focus(), 100);
    } catch (e) {
      if (e?.response?.data?.message) message.error(e.response.data.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiService.fuelRuns.delete(id);
      message.success('Deleted');
      fetchRuns();
    } catch {
      message.error('Failed to delete');
    }
  };

  const isEditing = (record) => record._id === editingKey;
  const grandTotal = runs.reduce((s, r) => s + (r.amount || 0), 0);

  const columns = [
    {
      title: 'Driver',
      dataIndex: 'driver',
      key: 'driver',
      render: (d, record) =>
        isEditing(record) ? (
          <Form.Item name="driver" noStyle rules={[{ required: true, message: ' ' }]}>
            <Select ref={firstFieldRef} size="small" style={{ width: 180 }} showSearch placeholder="Driver" optionFilterProp="children">
              {drivers.map(dr => <Option key={dr._id} value={dr._id}>{dr.name}</Option>)}
            </Select>
          </Form.Item>
        ) : d?.name || <Text type="secondary">—</Text>,
    },
    {
      title: 'Amount (£)',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="amount" noStyle rules={[{ required: true, message: ' ' }]}>
            <InputNumber size="small" style={{ width: 110 }} min={0} precision={2} placeholder="0.00" />
          </Form.Item>
        ) : <Text strong style={{ color: '#3f8600' }}>£{(v || 0).toFixed(2)}</Text>,
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="notes" noStyle>
            <Input size="small" placeholder="Optional notes" />
          </Form.Item>
        ) : v || <Text type="secondary">—</Text>,
    },
    {
      title: '',
      key: 'actions',
      width: 72,
      render: (_, record) =>
        isEditing(record) ? (
          <Space size={2}>
            <Button type="text" size="small" icon={<CheckOutlined style={{ color: '#3f8600' }} />} onClick={() => saveEdit(record)} />
            <Button type="text" size="small" icon={<CloseOutlined />} onClick={cancelEdit} />
          </Space>
        ) : (
          <Space size={2}>
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEdit(record)} />
            <Popconfirm title="Delete this entry?" onConfirm={() => handleDelete(record._id)} okText="Yes" cancelText="No" okButtonProps={{ danger: true }}>
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack} />
          <CalendarOutlined style={{ fontSize: 16 }} />
          <Title level={4} style={{ margin: 0 }}>{dayjsDate.format('ddd DD MMM YYYY')} — Fuel</Title>
        </Space>
      </div>

      {/* Add row */}
      <Card
        size="small"
        style={{ marginBottom: 12, borderTop: `2px solid ${COLOR_PRIMARY}`, background: 'rgba(22,119,255,0.06)' }}
        bodyStyle={{ padding: '10px 12px' }}
      >
        <Form form={addForm} onFinish={handleAdd}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div style={labelStyle}>Driver *</div>
              <Form.Item name="driver" noStyle rules={[{ required: true, message: ' ' }]}>
                <Select ref={firstFieldRef} size="small" style={{ width: 180 }} showSearch placeholder="Driver" optionFilterProp="children">
                  {drivers.map(dr => <Option key={dr._id} value={dr._id}>{dr.name}</Option>)}
                </Select>
              </Form.Item>
            </div>
            <div>
              <div style={labelStyle}>Amount (£) *</div>
              <Form.Item name="amount" noStyle rules={[{ required: true, message: ' ' }]}>
                <InputNumber size="small" style={{ width: 110 }} min={0} precision={2} placeholder="0.00" />
              </Form.Item>
            </div>
            <div>
              <div style={labelStyle}>Notes</div>
              <Form.Item name="notes" noStyle>
                <Input size="small" style={{ width: 220 }} placeholder="Optional notes" onPressEnter={handleAdd} />
              </Form.Item>
            </div>
            <div style={{ alignSelf: 'flex-end' }}>
              <Button type="primary" size="small" htmlType="submit" icon={<PlusOutlined />}>Add</Button>
            </div>
          </div>
        </Form>
      </Card>

      {/* Table */}
      <Form form={editForm} component={false}>
        <Table
          dataSource={runs}
          columns={columns}
          rowKey="_id"
          loading={loading}
          pagination={false}
          size="small"
          style={{ borderRadius: 8, overflow: 'hidden' }}
          locale={{ emptyText: 'No fuel entries yet — fill in the form above to add one' }}
          rowClassName={(record) => isEditing(record) ? 'daily-run-editing-row' : ''}
          summary={
            runs.length > 0
              ? () => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Total</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong style={{ color: '#3f8600' }}>£{grandTotal.toFixed(2)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} />
                  <Table.Summary.Cell index={3} />
                </Table.Summary.Row>
              )
              : undefined
          }
        />
      </Form>
    </div>
  );
};

// ─── Root ─────────────────────────────────────────────────────────────────────

const FuelRunList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDate = searchParams.get('date') || null;

  if (selectedDate) {
    return <DayDetailView date={selectedDate} onBack={() => setSearchParams({})} />;
  }
  return <DateListView onSelectDate={(d) => setSearchParams({ date: d })} />;
};

const labelStyle = { fontSize: 11, color: '#888', marginBottom: 2 };

export default FuelRunList;
